import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { deriveRoleFromEmail, useAuth } from "../context/AuthContext";
import { getSupabaseClient } from "../services/supabaseClient";
import { isAwaitingPasswordRecovery, recoveryLinkError } from "../services/passwordRecovery";
import { challengeMFA, listMFAFactors, verifyMFA } from "../services/mfaService";
import { evaluatePasswordStrength } from "../utils/passwordPolicy";
import { buildPublicAppUrl } from "../services/publicAppUrl";
import "../styles/auth.css";
import "../styles/password.css";

export default function PasswordResetPage({ reset = false }) {
    const { authEnabled, loading, recoveryUser, signOut } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [email, setEmail] = useState(location.state?.email || "");
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [visible, setVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [error, setError] = useState(null);
    const [factor, setFactor] = useState(null);
    const [factorsReady, setFactorsReady] = useState(false);
    const [factorAttempt, setFactorAttempt] = useState(0);
    const [code, setCode] = useState("");
    const recoveryId = recoveryUser?.id;
    const evaluation = useMemo(() => evaluatePasswordStrength(password, {
        role: recoveryUser?.user_metadata?.role || deriveRoleFromEmail(recoveryUser?.email),
    }), [password, recoveryUser]);

    useEffect(() => {
        if (!reset || !recoveryId || recoveryLinkError) return;
        let active = true;
        setFactorsReady(false);
        setError(null);
        listMFAFactors().then((data) => {
            if (!active) return;
            setFactor((data?.totp || data?.all || []).find((item) => item.status === "verified") || null);
            setFactorsReady(true);
        }).catch(() => {
            if (active) setError("Unable to check two-factor authentication. Please retry.");
        });
        return () => { active = false; };
    }, [reset, recoveryId, factorAttempt]);

    async function sendLink(event) {
        event.preventDefault();
        if (submitting || !authEnabled) return;
        setSubmitting(true);
        setError(null);
        try {
            const { error: requestError } = await getSupabaseClient().auth.resetPasswordForEmail(
                email.trim().toLowerCase(),
                { redirectTo: buildPublicAppUrl("/auth/reset-password") }
            );
            if (requestError) throw requestError;
            setSent(true);
        } catch (err) {
            // Never echo provider errors that could disclose whether an account exists.
            setError(err?.status === 429
                ? "Too many reset requests. Please wait a few minutes and try again."
                : "Unable to send a reset link. Please try again later.");
        } finally {
            setSubmitting(false);
        }
    }

    async function finishReset() {
        await signOut();
        navigate("/auth", { replace: true, state: { passwordReset: true } });
    }

    async function updatePassword(event) {
        event.preventDefault();
        if (submitting) return;
        setError(null);
        if (!updated && (!recoveryUser || recoveryLinkError || !factorsReady)) return;
        if (!updated && !evaluation.isValid) {
            setError("Choose a stronger password that meets all the requirements below.");
            return;
        }
        if (!updated && password !== confirmation) {
            setError("Passwords do not match.");
            return;
        }
        setSubmitting(true);
        let passwordChanged = updated;
        try {
            if (!updated) {
                if (factor) {
                    if (!/^\d{6}$/.test(code.trim())) throw new Error("Enter the 6-digit code from your authenticator app.");
                    const challenge = await challengeMFA({ factorId: factor.id });
                    await verifyMFA({ factorId: factor.id, challengeId: challenge.id, code: code.trim() });
                }
                const supabase = getSupabaseClient();
                // Prevent an account switch in another tab from updating the wrong user.
                const { data, error: userError } = await supabase.auth.getUser();
                if (userError || data?.user?.id !== recoveryId) {
                    throw new Error("This reset session is no longer valid. Please request a new link.");
                }
                const { error: updateError } = await supabase.auth.updateUser({ password });
                if (updateError) throw updateError;
                passwordChanged = true;
                setUpdated(true);
                setPassword("");
                setConfirmation("");
                setCode("");
            }
            await finishReset();
        } catch (err) {
            setError(passwordChanged
                ? "Your password was updated, but we could not finish signing out. Select Return to sign in to retry."
                : err?.message || "Unable to reset your password. Please request a new link.");
        } finally {
            setSubmitting(false);
        }
    }

    async function leaveRecovery() {
        setSubmitting(true);
        setError(null);
        try {
            if (recoveryUser || isAwaitingPasswordRecovery()) await signOut();
            navigate("/auth", { replace: true });
        } catch (_) {
            setError("Unable to sign out of password recovery. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <main className="auth-page">
            <div className="auth-shell auth-recovery-shell">
                <div className="auth-brand">
                    <img src="/images/be+accredited+group.webp" alt="Be Accredited Group" width="1473" height="304" />
                </div>
                <section className="auth-card" aria-labelledby="recovery-title">
                    <p className="auth-card-eyebrow">YOUR ACCOUNT</p>
                    <h1 id="recovery-title">{reset ? "Choose a new password" : "Forgot password?"}</h1>
                    {!authEnabled ? <p role="alert">Password reset is unavailable because authentication is not configured.</p>
                        : loading ? <p role="status">Checking your reset link…</p>
                        : !reset ? sent ? (
                            <p className="helper" role="status">If an account exists for that email, you’ll receive a password reset link. Check your inbox and spam folder.</p>
                        ) : (
                            <form className="auth-form" onSubmit={sendLink}>
                                <p className="auth-description">Enter your account email and we’ll send you a link to reset your password.</p>
                                <label className="form-field"><span>Email</span>
                                    <input type="email" name="email" autoComplete="email" value={email}
                                        onChange={(event) => setEmail(event.target.value)} required disabled={submitting} />
                                </label>
                                <button className="btn primary" disabled={submitting || !email.trim()}>
                                    {submitting ? "Sending…" : "Send reset link"}
                                </button>
                            </form>
                        ) : updated ? (
                            <form className="auth-form" onSubmit={updatePassword}>
                                <p role="status">Your password has been updated.</p>
                                <button className="btn primary" disabled={submitting}>Return to sign in</button>
                            </form>
                        ) : !recoveryUser || recoveryLinkError ? (
                            <div>
                                <p role="alert">This reset link is invalid or has expired. Request a new link to reset your password.</p>
                                <Link className="auth-recovery-link" to="/auth/forgot-password">Request a new reset link</Link>
                            </div>
                        ) : (
                            <form className="auth-form" onSubmit={updatePassword}>
                                <p className="auth-description">Choose a strong password for your account. You’ll sign in again after resetting it.</p>
                                <label className="form-field"><span>New password</span>
                                    <input type={visible ? "text" : "password"} autoComplete="new-password" value={password}
                                        onChange={(event) => setPassword(event.target.value)} required disabled={submitting} />
                                </label>
                                <label className="form-field"><span>Confirm new password</span>
                                    <input type={visible ? "text" : "password"} autoComplete="new-password" value={confirmation}
                                        onChange={(event) => setConfirmation(event.target.value)} required disabled={submitting} />
                                </label>
                                <button type="button" className="btn ghost" aria-pressed={visible} onClick={() => setVisible(!visible)}>
                                    {visible ? "Hide passwords" : "Show passwords"}
                                </button>
                                <div className="password-strength">
                                    <p>Strength: {evaluation.scoreLabel} ({evaluation.score}/4)</p>
                                    <p className="smallprint">Use at least {evaluation.minLength} characters, an uppercase letter, a lowercase letter, a number and a special character (!@#$%^&amp;*). Aim for a strength score of {evaluation.requiredScore}+.</p>
                                </div>
                                {factor && <div className="form-field"><label htmlFor="reset-mfa-code">Authentication code</label>
                                    <input id="reset-mfa-code" aria-describedby="reset-mfa-help" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
                                        value={code} onChange={(event) => setCode(event.target.value)} required disabled={submitting} />
                                    <span id="reset-mfa-help" className="smallprint">Enter the 6-digit code from your authenticator app.</span>
                                </div>}
                                {!factorsReady && (error
                                    ? <button type="button" className="btn ghost" onClick={() => setFactorAttempt(factorAttempt + 1)}>Retry security check</button>
                                    : <p role="status">Checking account security…</p>)}
                                <button className="btn primary" disabled={submitting || !factorsReady || !password || !confirmation}>
                                    {submitting ? "Resetting…" : "Reset password"}
                                </button>
                            </form>
                        )}
                    {error && <p className="error-message" role="alert">{error}</p>}
                    {!updated && <button type="button" className="btn ghost auth-recovery-back" disabled={submitting} onClick={leaveRecovery}>Back to sign in</button>}
                </section>
            </div>
        </main>
    );
}
